import React, { FC, useMemo } from 'react'
import Grid from '@material-ui/core/Grid'
import Box from '@material-ui/core/Box'
import MenuItem from '@material-ui/core/MenuItem'
import Typography from '@material-ui/core/Typography'
import { useFormik } from 'formik'
import { isToday } from 'date-fns'
import Button from 'UI/Button/ButtonWithPreloader'
import SelectComponent from 'UI/Select'
import { paymentDays } from 'constants/paymentDay'
import { PAYMENT_DAY_EARLIER_THAN_TODAY } from 'constants/errors'
import { IScheduleListItem } from 'typescript/interfaces/deals'
import { IPaymentDay } from 'typescript/interfaces/paymentDay'
import { schemaSchedulePaymentDay } from 'components/SchedulePaymentDayForm/schema'
import { convertDate } from 'utils/formatters'
import { getRescheduledPaymentDate, isEarlierInWeek, isPaymentDueToday } from 'utils/weeklyPaymentDay'

interface ISchedulePaymentDayFormProps {
  schedule: IScheduleListItem
  cancel: () => void
  submit: (paymentDay: IPaymentDay) => void
  loading?: boolean
}

interface ISchedulePaymentDayFormValues {
  paymentDay: IPaymentDay | ''
}

const SchedulePaymentDayForm: FC<ISchedulePaymentDayFormProps> = ({ schedule, cancel, submit, loading }) => {
  const isDueToday = useMemo(() => isPaymentDueToday(schedule), [schedule])

  const { handleSubmit, values, errors, touched, handleChange } = useFormik<ISchedulePaymentDayFormValues>({
    initialValues: {
      paymentDay: schedule.paymentDay || '',
    },
    validationSchema: schemaSchedulePaymentDay,
    onSubmit: ({ paymentDay }) => {
      if (paymentDay) submit(paymentDay)
    },
  })

  const isChanged = Boolean(values.paymentDay) && values.paymentDay !== schedule.paymentDay

  const rescheduledPaymentDate = useMemo(
    () => (values.paymentDay && isChanged ? getRescheduledPaymentDate(schedule, values.paymentDay) : null),
    [schedule, values.paymentDay, isChanged]
  )

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={9}>
        <Grid item xs={12}>
          <SelectComponent
            value={values.paymentDay}
            name="paymentDay"
            label="Day of Week"
            onChange={handleChange}
            error={Boolean(touched.paymentDay && errors.paymentDay)}
            helperText={touched.paymentDay && errors.paymentDay}
          >
            {paymentDays.map(day => (
              <MenuItem key={day} value={day} disabled={isDueToday && isEarlierInWeek(day)}>
                {day}
              </MenuItem>
            ))}
          </SelectComponent>
        </Grid>
        {isDueToday && (
          <Grid item xs={12}>
            <Typography color="textSecondary">{PAYMENT_DAY_EARLIER_THAN_TODAY}</Typography>
          </Grid>
        )}
        {rescheduledPaymentDate && (
          <Grid item xs={12}>
            <Typography color="textSecondary">
              {isToday(rescheduledPaymentDate)
                ? 'The payment will be processed today.'
                : `The next payment will be processed on ${convertDate(rescheduledPaymentDate)}.`}
            </Typography>
          </Grid>
        )}
      </Grid>
      <Box mt={10}>
        <Grid container spacing={4}>
          <Grid item xs={6}>
            <Button onClick={cancel} fullWidth color="secondary" variant="contained">
              Cancel
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button loading={loading} disabled={!isChanged} fullWidth color="primary" variant="contained" type="submit">
              Save
            </Button>
          </Grid>
        </Grid>
      </Box>
    </form>
  )
}

export default SchedulePaymentDayForm
